// Audio Context Investigation & Diagnostics
// This module helps identify multiple AudioContext instances, routing issues, and connection problems

function AudioContextDiagnostics() {
  this.contexts = new Map();
  this.contextCounter = 0;
  this.routingLogs = [];
  this.isMonitoring = false;
  
  // Track all AudioContext creations globally
  this.wrapAudioContextConstructor();
}

AudioContextDiagnostics.prototype.wrapAudioContextConstructor = function() {
  if (typeof window === 'undefined') return;
  
  const originalAudioContext = window.AudioContext || window.webkitAudioContext;
  const diagnostics = this;
  
  function TrackedAudioContext(...args) {
    const context = new originalAudioContext(...args);
    diagnostics.registerContext(context);
    return context;
  }
  
  // Copy static properties
  Object.setPrototypeOf(TrackedAudioContext, originalAudioContext);
  TrackedAudioContext.prototype = originalAudioContext.prototype;
  
  window.AudioContext = TrackedAudioContext;
  if (window.webkitAudioContext) {
    window.webkitAudioContext = TrackedAudioContext;
  }
};

// Simple Audio Context Investigation & Diagnostics
// Focus on detecting multiple contexts and basic routing issues

let contextCounter = 0;
const activeContexts = new Map();
const routingLogs = [];

// Simple wrapper to track AudioContext creation
function trackAudioContextCreation() {
  if (typeof window === 'undefined') return;
  
  const originalAudioContext = window.AudioContext || window.webkitAudioContext;
  
  function TrackedAudioContext(...args) {
    const context = new originalAudioContext(...args);
    const id = ++contextCounter;
    
    activeContexts.set(id, {
      id,
      context,
      createdAt: new Date().toISOString(),
      sampleRate: context.sampleRate,
      state: context.state
    });
    
    console.log(`🔍 [DIAGNOSTICS] AudioContext ${id} created (Total: ${activeContexts.size})`);
    
    // Monitor state changes
    context.addEventListener('statechange', () => {
      const info = activeContexts.get(id);
      if (info) {
        info.state = context.state;
        console.log(`� [DIAGNOSTICS] AudioContext ${id} state: ${context.state}`);
      }
    });
    
    return context;
  }
  
  // Replace the constructor
  window.AudioContext = TrackedAudioContext;
  if (window.webkitAudioContext) {
    window.webkitAudioContext = TrackedAudioContext;
  }
}

// Initialize tracking immediately
trackAudioContextCreation();

// Generate diagnostics report
export function getAudioDiagnosticsReport() {
  const runningContexts = Array.from(activeContexts.values())
    .filter(info => info.context.state === 'running');
  
  const issues = [];
  
  if (activeContexts.size > 1) {
    issues.push({
      type: 'MULTIPLE_CONTEXTS',
      severity: 'HIGH',
      description: `${activeContexts.size} AudioContexts exist, ${runningContexts.length} running`
    });
  }
  
  return {
    timestamp: new Date().toISOString(),
    totalContexts: activeContexts.size,
    runningContexts: runningContexts.length,
    contexts: Array.from(activeContexts.values()).map(info => ({
      id: info.id,
      state: info.context.state,
      sampleRate: info.sampleRate,
      createdAt: info.createdAt
    })),
    connectionCount: routingLogs.length,
    potentialIssues: issues
  };
}

// Simple audio monitoring function
export function startAudioDiagnostics(audioElement) {
  console.log('🔍 [DIAGNOSTICS] Starting audio diagnostics...');
  
  if (audioElement) {
    // Basic audio element monitoring
    const monitorAudioElement = () => {
      if (!audioElement) return;
      
      const state = {
        paused: audioElement.paused,
        currentTime: audioElement.currentTime.toFixed(2),
        volume: audioElement.volume,
        readyState: audioElement.readyState,
        error: audioElement.error?.code || null
      };
      
      if (audioElement.error) {
        console.error('� [DIAGNOSTICS] Audio element error:', state);
      }
    };
    
    // Monitor every 5 seconds
    setInterval(monitorAudioElement, 5000);
  }
  
  return getAudioDiagnosticsReport();
}

// Reset diagnostics
export function resetAudioDiagnostics() {
  activeContexts.clear();
  routingLogs.length = 0;
  contextCounter = 0;
  console.log('🔍 [DIAGNOSTICS] Diagnostics reset');
}
